import { IPaymentOfferModel } from "@nf/common";
import { ROUTES } from "@nf/constants";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "next-i18next";

import { useIsAuthenticated, useOffers } from "hooks";
import { Divider, OfferCard } from "components";
import { VoucherLink } from "../VoucherLink/VoucherLink";

import styles from "./ChooseSubscription.module.scss";
import Button from "../../../../components/Button";
import { useCreatePaymentOrder } from "../../hooks/useCreatePaymentOrder";
import { useBuildLocalizedAbsolutedUrl } from "hooks";
import { PaymentError } from "../PaymentError";
import PaymentHeader from "../PaymentHeader";

export const ChooseSubscription = () => {
  const { isRegisteredViaCampaign } = useIsAuthenticated();
  const { t } = useTranslation("translations");

  const [selectedOffer, setSelectedOffer] = useState<IPaymentOfferModel | null>(
    null
  );
  const { offers } = useOffers();
  const hasOffers = !!offers?.length;

  const buildLocalizedAbsolutedUrl = useBuildLocalizedAbsolutedUrl();
  const {
    mutateAsync: createPaymentOrder,
    isLoading: createPaymentOrderLoading,
    error: createPaymentOrderError,
  } = useCreatePaymentOrder();

  const selectOffer = useCallback(
    (offer: IPaymentOfferModel) => setSelectedOffer(offer),
    [setSelectedOffer]
  );

  const renderOffer = useCallback(
    (offer: IPaymentOfferModel) => (
      <OfferCard
        key={offer.Id}
        offer={offer}
        active={offer.Id === selectedOffer?.Id}
        handleSelect={selectOffer}
      />
    ),
    [selectedOffer]
  );

  const goToCheckout = async () => {
    if (!selectedOffer?.Id || !selectedOffer?.Handle) {
      return;
    }

    try {
      const paymentOrder = await createPaymentOrder({
        OfferId: selectedOffer.Id.toString(),
        OfferHandle: selectedOffer.Handle,
        AcceptUrl: buildLocalizedAbsolutedUrl(
          ROUTES.ASSIGNING_SUBSCRIPTION_SCREEN
        ),
        CancelUrl: buildLocalizedAbsolutedUrl(ROUTES.PAYMENT_SUBSCRIPTION),
      });

      if (paymentOrder?.CheckoutUrl) {
        window.location.href = paymentOrder.CheckoutUrl;
      }
    } catch {}
  };

  const buttonState = createPaymentOrderLoading
    ? "loading"
    : !selectedOffer
    ? "disabled"
    : "default";

  useEffect(() => {
    const defaultOffer = offers?.find((offer) => offer.Default);
    if (defaultOffer) {
      selectOffer(defaultOffer);
    }
  }, [offers, selectOffer]);

  return (
    <div className={styles.container}>
      <PaymentHeader />

      <div className={styles.offers}>{offers?.map(renderOffer)}</div>

      <span className={styles.description}>
        {!!offers?.length
          ? t("web_payment_choose_subscription_description")
          : t("web_payment_choose_subscription_error")}
      </span>

      {createPaymentOrderError && (
        <div className="mt-md mb-md">
          <PaymentError
            error={createPaymentOrderError.Message?.toString() || ""}
          />
        </div>
      )}

      <Button
        size="medium"
        variant="primary"
        state={buttonState}
        className={styles.button}
        onClick={goToCheckout}
      >
        {t("web_payment_select_subscription_button")}
      </Button>

      <Divider className={styles.divider} />

      {hasOffers && !isRegisteredViaCampaign && <VoucherLink />}
    </div>
  );
};

export default ChooseSubscription;
